import React, { FormEvent, useState } from 'react';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  ActionList,
  ActionListItem,
  Alert,
  ArrowLeftIcon,
  Box,
  Button,
  Card,
  CardBody,
  Dropdown,
  DropdownOverlay,
  InfoIcon,
  Link,
  PasswordInput,
  SelectInput,
  Switch,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { TDSUpdateRequest } from 'api/companyOnboarding';
import api from 'api';
import { fetchLoggedInUser } from 'reducers/loggedInUser';
import { AppError } from 'utils/AppError';
import Errors from 'components/ui/Form/Errors';
import { routePaths } from 'components/Routes/data';
import { getNextFourQuarters } from './utils';
import InviteCA from './InviteCA';
import { trackSegment } from 'utils/segment';
import { TAN_REGEX } from "../../../../utils/regexes";

const START_FROM_IMMEDEATELY = 'immedeately';

const PasswordFieldScope = styled.div(
  ({ theme }) => `
  button[aria-label='Show password'],
  button[aria-label='Hide password'] {
    width: ${theme.spacing[5]}px !important;
    min-width: ${theme.spacing[5]}px !important;
    max-width: ${theme.spacing[5]}px !important;
    flex: 0 0 ${theme.spacing[5]}px !important;
  }`,
);

const TdsSetupForm = styled.form(
  ({ theme }) => `
  width: 100%;
  padding-top: ${theme.spacing[10]}px;
  padding-bottom: ${theme.spacing[10]}px;
`,
);

const TooltipPopover = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  z-index: 1000;
  pointer-events: none;
  min-width: 280px;
  padding: ${({ theme }) => theme.spacing[3]}px;
  border-radius: ${({ theme }) => theme.border.radius.medium}px;
  background-color: ${({ theme }) => theme.colors.popup.background.intense};
  box-shadow: ${({ theme }) => theme.elevation.midRaised};
`;

const TdsSetup = () => {
  const currentOrganization = useSelector(loggedInUserSelectors.currentOrganization);
  const dispatch = useDispatch();
  const history = useHistory();
  const [tan, setTan] = useState(currentOrganization?.tan || '');
  const [isSalaryTDSEnabled, setIsSalaryTDSEnabled] = useState(
    !!currentOrganization?.paymentOptions?.payTds,
  );
  const [isSalaryTooltipOpen, setIsSalaryTooltipOpen] = useState(false);
  const [isNonSalaryTooltipOpen, setIsNonSalaryTooltipOpen] = useState(false);
  const [isAutoFile24QTooltipOpen, setIsAutoFile24QTooltipOpen] = useState(false);
  const [isAutoFile26QTooltipOpen, setIsAutoFile26QTooltipOpen] = useState(false);
  const [isNonSalaryTDSEnabled, setIsNonSalaryTDSEnabled] = useState(
    !!currentOrganization?.paymentOptions?.enableTdsPaymentForContractors,
  );
  const [tracesUsername, setTracesUsername] = useState(
    currentOrganization?.externalCredentials?.tracesUser || '',
  );
  const [tracesPassword, setTracesPassword] = useState(
    currentOrganization?.externalCredentials?.tracesPass || '',
  );
  const [itPortalUsername, setItPortalUsername] = useState(
      currentOrganization?.externalCredentials?.itUser || '',
  );
  const [itPortalPassword, setItPortalPassword] = useState(
      currentOrganization?.externalCredentials?.itPass || '',
  );
  const links = useSelector(loggedInUserSelectors.referenceLinks);
  const [shouldAutoFile24Q, setShouldAutoFile24Q] = useState(!!currentOrganization?.auto24q);
  const [shouldAutoFile26Q, setShouldAutoFile26Q] = useState(!!currentOrganization?.auto26q);
  const [start24QFrom, setStart24QFrom] = useState(
    currentOrganization?.paymentOptions?.start24QFrom || START_FROM_IMMEDEATELY,
  );
  const [start26QFrom, setStart26QFrom] = useState(
    currentOrganization?.paymentOptions?.start26QFrom || START_FROM_IMMEDEATELY,
  );
  const nextFourQuarters = getNextFourQuarters();
  const hasInvitedCA = !!currentOrganization?.onboardingConfig?.hasInvitedCA;

  const updateTDSMutation = useMutation(
    ({
      isSalaryTDSEnabled,
      isNonSalaryTDSEnabled,
      itPortalUsername,
      itPortalPassword,
      tracesUsername,
      tracesPassword,
      shouldAutoFile24Q,
      shouldAutoFile26Q,
      start24QFrom,
      start26QFrom,
      tan,
    }: TDSUpdateRequest) =>
      api.companyOnboarding.updateTDSSetup({
        isSalaryTDSEnabled,
        isNonSalaryTDSEnabled,
        itPortalUsername,
        itPortalPassword,
        tracesUsername,
        tracesPassword,
        shouldAutoFile24Q,
        shouldAutoFile26Q,
        start24QFrom,
        start26QFrom,
        tan,
      }),
    {
      onSuccess: (data) => {
        trackSegment("onboarding.setup_compliance.tds.save_and_next_click");
        dispatch(fetchLoggedInUser());
        history.push(routePaths.companyOnboarding.compliance.pt);
      },
      onError: (error: typeof AppError) => {},
    },
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!isSalaryTDSEnabled) {
        setShouldAutoFile24Q(false);
    }

    if (!isNonSalaryTDSEnabled) {
        setShouldAutoFile26Q(false);
    }

    let start24QFromRequest: string | null = start24QFrom;
    let start26QFromRequest: string | null = start26QFrom;

    if (start24QFromRequest === START_FROM_IMMEDEATELY) {
      start24QFromRequest = null;
    }

    if (start26QFromRequest === START_FROM_IMMEDEATELY) {
      start26QFromRequest = null;
    }

    let payLoad = {
      isSalaryTDSEnabled,
      isNonSalaryTDSEnabled,
      shouldAutoFile24Q,
      shouldAutoFile26Q,
      start24QFrom: start24QFromRequest,
      start26QFrom: start26QFromRequest,
    };

    if (isNonSalaryTDSEnabled || isSalaryTDSEnabled ) {
      const extraKeys = {
        itPortalUsername,
        itPortalPassword,
        tracesUsername,
        tracesPassword,
        tan,
      }
      payLoad = {...payLoad,...extraKeys};
    }

    updateTDSMutation.mutate(payLoad);
    
  };

  const isFormBeingSubmitted = updateTDSMutation.status === 'loading';
  const isTanInvalid = tan.length > 0 && !new RegExp(TAN_REGEX).test(tan);

  return (
    <TdsSetupForm onSubmit={handleSubmit}>
      <Box
        display="flex"
        flexDirection="column"
        gap="spacing.8"
        paddingBottom="spacing.8"
        width="100%"
        maxWidth="512px"
        marginX="auto"
      >
        {updateTDSMutation.status === 'error' && (
          <Alert
            color="negative"
            description={<Errors errorResponse={updateTDSMutation.error} />}
            isDismissible={false}
            isFullWidth
          />
        )}
        {!hasInvitedCA && <InviteCA />}

        {(isSalaryTDSEnabled || isNonSalaryTDSEnabled) && (
        <Card elevation="none" borderRadius="large" backgroundColor="surface.background.gray.intense">
          <CardBody>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap="spacing.6"
              width="100%"
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap="spacing.2"
                width="100%"
              >
                <Text size="large" weight="semibold" textAlign="left">
                  Your company's TAN
                </Text>
                <Text size="medium" color="surface.text.gray.muted" textAlign="left">
                  Your 10 character tax deduction account number, needed to file TDS returns
                </Text>
              </Box>
              <Box width="100%">
                <TextInput
                  label={'TAN '}
                  name="company-tan"
                  placeholder="Company TAN number"
                  value={tan}
                  onChange={({ value }) => setTan(value ?? '')}
                  validationState={isTanInvalid ? 'error' : 'none'}
                  errorText="Invalid or Empty TAN"
                  isDisabled={isFormBeingSubmitted}
                  isRequired
                  necessityIndicator="required"
                  helpText={
                    (
                      <>
                        Not sure if this is correct? Check on the{' '}
                        <Link
                          size="small"
                          href={links.verifyTanLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() =>
                            trackSegment(
                              'onboarding.company_details.tax_setup.verify_your_tan_here_click',
                            )
                          }
                        >
                          income tax portal
                        </Link>
                      </>
                    ) as unknown as string
                  }
                />
              </Box>
            </Box>
          </CardBody>
        </Card>
        )}

        {/* Card 3 — Which payments should have TDS? */}
        <Card elevation="none" borderRadius="large" backgroundColor="surface.background.gray.intense">
          <CardBody>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap="spacing.6"
              width="100%"
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap="spacing.2"
                width="100%"
              >
                <Text size="large" weight="semibold" textAlign="left">
                  Which payments should have TDS?
                </Text>
                <Text size="medium" color="surface.text.gray.muted" textAlign="left">
                  Turn on TDS for the payment types you run. It is easy to update later
                </Text>
              </Box>

              <Box
                width="100%"
                backgroundColor="surface.background.gray.intense"
                borderWidth="thin"
                borderColor="surface.border.gray.muted"
                borderRadius="large"
                padding="spacing.5"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap="spacing.4"
              >
                <Box display="flex" flexDirection="row" alignItems="center" gap="spacing.3" flex="1">
                  <Text size="medium" weight="semibold" textAlign="left">
                    Salary Payments
                  </Text>
                  <Box position="relative" display="inline-flex">
                    <Box
                      display="inline-flex"
                      alignItems="center"
                      onMouseEnter={() => setIsSalaryTooltipOpen(true)}
                      onMouseLeave={() => setIsSalaryTooltipOpen(false)}
                    >
                      <InfoIcon size="medium" color="surface.icon.gray.muted" />
                    </Box>
                    {isSalaryTooltipOpen && (
                      <TooltipPopover>
                        <Text size="small" color="surface.text.staticWhite.normal" textAlign="left">
                          TDS on employee salaries under Section 192. Filed quarterly via Form 24Q, and Form 138 from FY 2026-27 onward, with Form 16 generated at year end
                        </Text>
                      </TooltipPopover>
                    )}
                  </Box>
                </Box>
                <Switch
                  accessibilityLabel="Salary Payments"
                  isChecked={isSalaryTDSEnabled}
                  isDisabled={isFormBeingSubmitted}
                  onChange={({ isChecked }) => setIsSalaryTDSEnabled(isChecked)}
                />
              </Box>

              <Box
                width="100%"
                backgroundColor="surface.background.gray.intense"
                borderWidth="thin"
                borderColor="surface.border.gray.muted"
                borderRadius="large"
                padding="spacing.5"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap="spacing.4"
              >
                <Box display="flex" flexDirection="row" alignItems="center" gap="spacing.3" flex="1">
                  <Text size="medium" weight="semibold" textAlign="left">
                    Non-Salary Payments
                  </Text>
                  <Box position="relative" display="inline-flex">
                    <Box
                      display="inline-flex"
                      alignItems="center"
                      onMouseEnter={() => setIsNonSalaryTooltipOpen(true)}
                      onMouseLeave={() => setIsNonSalaryTooltipOpen(false)}
                    >
                      <InfoIcon size="medium" color="surface.icon.gray.muted" />
                    </Box>
                    {isNonSalaryTooltipOpen && (
                      <TooltipPopover>
                        <Text size="small" color="surface.text.staticWhite.normal" textAlign="left">
                          TDS on contractor, vendor, rent or professional service payouts (Sections 194C/194I/194J). Filed quarterly via Form 26Q, and Form 140 from FY 2026-27 onward
                        </Text>
                      </TooltipPopover>
                    )}
                  </Box>
                </Box>
                <Switch
                  accessibilityLabel="Non-Salary Payments"
                  isChecked={isNonSalaryTDSEnabled}
                  isDisabled={isFormBeingSubmitted}
                  onChange={({ isChecked }) => setIsNonSalaryTDSEnabled(isChecked)}
                />
              </Box>

              <Text size="small" color="surface.text.gray.muted" textAlign="left">
                Turning off a payment type stops new TDS filings. Past payslip deductions are
                managed in payslip settings
              </Text>
            </Box>
          </CardBody>
        </Card>


        {/* Card 4 — Auto file quarterly TDS returns */}
        {(isSalaryTDSEnabled || isNonSalaryTDSEnabled) && (
          <Card elevation="none" borderRadius="large" backgroundColor="surface.background.gray.intense">
            <CardBody>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap="spacing.6"
                width="100%"
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap="spacing.2"
                  width="100%"
                >
                  <Text size="large" weight="semibold" textAlign="left">
                    Auto file quarterly TDS returns
                  </Text>
                  <Text size="medium" color="surface.text.gray.muted" textAlign="left">
                    We'll submit your applicable salary and non-salary TDS returns every quarter, nothing to upload manually.
                  </Text>
                </Box>

                {isSalaryTDSEnabled && (
                  <Box
                    width="100%"
                    backgroundColor="surface.background.gray.intense"
                    borderWidth="thin"
                    borderColor="surface.border.gray.muted"
                    borderRadius="large"
                    padding="spacing.5"
                    display="flex"
                    flexDirection="column"
                    gap="spacing.5"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap="spacing.4"
                      width="100%"
                    >
                      <Box display="flex" flexDirection="row" alignItems="center" gap="spacing.3" flex="1">
                        <Text size="medium" weight="semibold" textAlign="left">
                          Auto file salary TDS returns
                        </Text>
                        <Box position="relative" display="inline-flex">
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            onMouseEnter={() => setIsAutoFile24QTooltipOpen(true)}
                            onMouseLeave={() => setIsAutoFile24QTooltipOpen(false)}
                          >
                            <InfoIcon size="medium" color="surface.icon.gray.muted" />
                          </Box>
                          {isAutoFile24QTooltipOpen && (
                            <TooltipPopover>
                              <Text size="small" color="surface.text.staticWhite.normal" textAlign="left">
                                We file your quarterly salary TDS return on your behalf. This uses Form 24Q for older years and Form 138 from FY 2026-27 onward.
                              </Text>
                            </TooltipPopover>
                          )}
                        </Box>
                      </Box>
                      <Switch
                        id="auto-24q"
                        accessibilityLabel="Auto file salary TDS returns"
                        isChecked={shouldAutoFile24Q}
                        isDisabled={isFormBeingSubmitted}
                        onChange={({ isChecked }) => setShouldAutoFile24Q(isChecked)}
                      />
                    </Box>
                    {shouldAutoFile24Q && (
                      <Dropdown selectionType="single">
                        <SelectInput
                          label="Start salary TDS auto filing from"
                          name="start-24q-from"
                          isDisabled={isFormBeingSubmitted}
                          value={start24QFrom}
                          onChange={({ values }) =>
                            setStart24QFrom(values[0] || START_FROM_IMMEDEATELY)
                          }
                          helpText="Auto filing salary TDS also enables Form 16 generation for your employees"
                        />
                        <DropdownOverlay>
                          <ActionList>
                            <ActionListItem title="Immediately" value={START_FROM_IMMEDEATELY} />
                            {nextFourQuarters.map((quarter) => (
                              <ActionListItem
                                key={`24q-${quarter.quarter}-${quarter.financialYear}`}
                                title={`Q${quarter.quarter}, FY ${quarter.financialYear} - ${quarter.financialYear + 1}`}
                                value={`Q${quarter.quarter}-${quarter.financialYear}`}
                              />
                            ))}
                          </ActionList>
                        </DropdownOverlay>
                      </Dropdown>
                    )}
                  </Box>
                )}

                {isNonSalaryTDSEnabled && (
                  <Box
                    width="100%"
                    backgroundColor="surface.background.gray.intense"
                    borderWidth="thin"
                    borderColor="surface.border.gray.muted"
                    borderRadius="large"
                    padding="spacing.5"
                    display="flex"
                    flexDirection="column"
                    gap="spacing.5"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap="spacing.4"
                      width="100%"
                    >
                      <Box display="flex" flexDirection="row" alignItems="center" gap="spacing.3" flex="1">
                        <Text size="medium" weight="semibold" textAlign="left">
                          Auto file non-salary TDS returns
                        </Text>
                        <Box position="relative" display="inline-flex">
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            onMouseEnter={() => setIsAutoFile26QTooltipOpen(true)}
                            onMouseLeave={() => setIsAutoFile26QTooltipOpen(false)}
                          >
                            <InfoIcon size="medium" color="surface.icon.gray.muted" />
                          </Box>
                          {isAutoFile26QTooltipOpen && (
                            <TooltipPopover>
                              <Text size="small" color="surface.text.staticWhite.normal" textAlign="left">
                                We file your quarterly non-salary TDS return on your behalf. This uses Form 26Q for older years and Form 140 from FY 2026-27 onward.
                              </Text>
                            </TooltipPopover>
                          )}
                        </Box>
                      </Box>
                      <Switch
                        id="auto-26q"
                        accessibilityLabel="Auto file non-salary TDS returns"
                        isChecked={shouldAutoFile26Q}
                        isDisabled={isFormBeingSubmitted}
                        onChange={({ isChecked }) => setShouldAutoFile26Q(isChecked)}
                      />
                    </Box>
                    {shouldAutoFile26Q && (
                      <Dropdown selectionType="single">
                        <SelectInput
                          label="Start non-salary TDS auto filing from"
                          name="start-26q-from"
                          isDisabled={isFormBeingSubmitted}
                          value={start26QFrom}
                          onChange={({ values }) =>
                            setStart26QFrom(values[0] || START_FROM_IMMEDEATELY)
                          }
                        />
                        <DropdownOverlay>
                          <ActionList>
                            <ActionListItem title="Immediately" value={START_FROM_IMMEDEATELY} />
                            {nextFourQuarters.map((quarter) => (
                              <ActionListItem
                                key={`26q-${quarter.quarter}-${quarter.financialYear}`}
                                title={`Q${quarter.quarter}, FY ${quarter.financialYear} - ${quarter.financialYear + 1}`}
                                value={`Q${quarter.quarter}-${quarter.financialYear}`}
                              />
                            ))}
                          </ActionList>
                        </DropdownOverlay>
                      </Dropdown>
                    )}
                  </Box>
                )}
              </Box>
            </CardBody>
          </Card>
        )}

        {/* Card 5 — Link your government portals */}
        {(isSalaryTDSEnabled || isNonSalaryTDSEnabled) && (
          <Card elevation="none" borderRadius="large" backgroundColor="surface.background.gray.intense">
            <CardBody>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap="spacing.6"
                width="100%"
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap="spacing.2"
                  width="100%"
                >
                  <Text size="large" weight="semibold" textAlign="left">
                    Link your government portals
                  </Text>
                  <Text size="medium" color="surface.text.gray.muted" textAlign="left">
                    We need these to file your returns and issue Form 16 to employees
                  </Text>
                </Box>

                {/* Income tax portal sub-card */}
                <Box
                  width="100%"
                  backgroundColor="surface.background.gray.intense"
                  borderWidth="thin"
                  borderColor="surface.border.gray.muted"
                  borderRadius="large"
                  padding="spacing.5"
                  display="flex"
                  flexDirection="column"
                  gap="spacing.5"
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    gap="spacing.1"
                    width="100%"
                  >
                    <Text size="medium" weight="semibold" textAlign="left">
                      Income tax portal
                    </Text>
                    <Text size="small" color="surface.text.gray.muted" textAlign="left">
                      Use your TAN as the user ID. Make sure you're registered as a{' '}
                      <Link
                        size="small"
                        href={links.itRegistrationStepsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        "Tax Deductor and Collector"
                      </Link>
                    </Text>
                  </Box>
                  <Box display="flex" flexDirection="row" gap="spacing.5" width="100%">
                    <Box flex="1">
                      <TextInput
                        label="User ID (Your TAN)"
                        name="it-username"
                        placeholder="User ID"
                        value={itPortalUsername}
                        onChange={({ value }) => setItPortalUsername(value ?? '')}
                        isDisabled={isFormBeingSubmitted}
                        autoCompleteSuggestionType="none"
                      />
                    </Box>
                    <Box flex="1">
                      <PasswordFieldScope>
                        <PasswordInput
                          label="Password"
                          name="it-password"
                          placeholder='Password'
                          value={itPortalPassword}
                          onChange={({ value }) => setItPortalPassword(value ?? '')}
                          isDisabled={isFormBeingSubmitted}
                        />
                      </PasswordFieldScope>
                    </Box>
                  </Box>
                </Box>

                {/* TRACES sub-card */}
                <Box
                  width="100%"
                  backgroundColor="surface.background.gray.intense"
                  borderWidth="thin"
                  borderColor="surface.border.gray.muted"
                  borderRadius="large"
                  padding="spacing.5"
                  display="flex"
                  flexDirection="column"
                  gap="spacing.5"
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    gap="spacing.1"
                    width="100%"
                  >
                    <Text size="medium" weight="semibold" textAlign="left">
                      TRACES
                    </Text>
                    <Text size="small" color="surface.text.gray.muted" textAlign="left">
                      For corrections and downloading Form 16/16A.{' '}
                      <Link
                        size="small"
                        href={links.linkTraces}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          trackSegment('onboarding.setup_compliance.tds.traces_click')
                        }
                      >
                        Learn about TRACES
                      </Link>
                    </Text>
                  </Box>
                  <Box display="flex" flexDirection="row" gap="spacing.5" width="100%">
                    <Box flex="1">
                      <TextInput
                        label="Username"
                        name="traces-username"
                        placeholder='Username'
                        value={tracesUsername}
                        onChange={({ value }) => setTracesUsername(value ?? '')}
                        isDisabled={isFormBeingSubmitted}
                      />
                    </Box>
                    <Box flex="1">
                      <PasswordFieldScope>
                        <PasswordInput
                          label="Password"
                          name="traces-password"
                          placeholder='Password'
                          value={tracesPassword}
                          onChange={({ value }) => setTracesPassword(value ?? '')}
                          isDisabled={isFormBeingSubmitted}
                        />
                      </PasswordFieldScope>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardBody>
          </Card>
        )}

        <Box
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          paddingTop="spacing.5"
        >
          <Link
            variant="button"
            color="neutral"
            icon={ArrowLeftIcon}
            iconPosition="left"
            onClick={() => history.push(routePaths.phpRoutes.companyOnboarding)}
          >
            Back to setup guide
          </Link>
          <Button
            variant="primary"
            type="submit"
            isLoading={isFormBeingSubmitted}
            isDisabled={
              isSalaryTDSEnabled || isNonSalaryTDSEnabled
                ? !tan || isTanInvalid
                : false
            }
          >
            Save & Next
          </Button>
        </Box>
      </Box>
    </TdsSetupForm>
  );
};

export default TdsSetup;
